
import { Provider } from "@/components/ui/provider"
import { Toaster } from "@/components/ui/toaster"
import { Box, Flex } from "@chakra-ui/react";
import { Route, Routes, BrowserRouter, useLocation, Navigate } from 'react-router-dom';
import RequireAdmin from './auth/RequireAdmin';
import Header from './components/Header'
import Footer from './components/Footer'
import Landing from './pages/Landing'
import Login from './pages/auth/Login'
import Info from './pages/Info'

import './App.css';
import { AuthProvider, useAuthContext } from './auth/AuthContext';
import OverviewPartners from './pages/partners/OverviewPartners';
import PartnersAdd from './pages/partners/PartnersAdd';
import OverviewProjects from './pages/projects/OverviewProjects';
import PartnersEdit from './pages/partners/PartnersEdit';
import PartnersView from './pages/partners/PartnersView';
import ProjectsAdd from './pages/projects/ProjectsAdd';
import ProjectsEdit from './pages/projects/ProjectsEdit';
import ProjectsView from './pages/projects/ProjectsView';
import OverviewDonationCodes from './pages/donations/OverviewDonationCodes';
import OverviewTransfers from './pages/funds/OverviewTransfers';
import TransfersAdd from './pages/funds/TransfersAdd';
import OverviewFunds from './pages/funds/OverviewFunds';
import FundsAdd from './pages/funds/FundsAdd';
import FundsEdit from './pages/funds/FundsEdit';
import FundsView from './pages/funds/FundsView';
import OverviewFundsMoves from './pages/funds/OverviewFundsMoves';
import FundMoveAdd from './pages/funds/FundMoveAdd';
import FundMoveEdit from './pages/funds/FundMoveEdit';
import FundMoveView from './pages/funds/FundMoveView';
import DashboardProvisions from './pages/funds/DashboardProvisions';
import OverviewCommitments from './pages/fundraising/OverviewCommitments';
import DashboardFundraisingAll from './pages/fundraising/DashboardFundraisingAll';
import ForgotPassword from './pages/auth/ForgotPassword';
import Profile from './pages/auth/Profile';
import { ReactNode, useEffect } from 'react';
import CommitmentsAdd from './pages/fundraising/CommitmentsAdd';
import OverviewDonors from './pages/fundraising/OverviewDonors';
import DonorForm from './pages/fundraising/DonorForm';
import CommitmentsEdit from './pages/fundraising/CommitmentsEdit';
import CommitmentsView from './pages/fundraising/CommitmentsView';
import OverviewExpenses from './pages/partners/OverviewExpenses';
import ExpensesAdd from './pages/partners/ExpensesAdd';
import ExpensesEdit from './pages/partners/ExpensesEdit';
import ExpensesView from './pages/partners/ExpensesView';
import Logout from './pages/auth/Logout';
import OverviewTransfersMoves from './pages/funds/OverviewTransfersMoves';
import TransfersMoveAdd from './pages/funds/TransfersMoveAdd';
import TransfersMoveEdit from './pages/funds/TransfersMoveEdit';
import TransfersMoveView from './pages/funds/TransfersMoveView';
import NotFound from './pages/NotFound';
import DashboardFundraisingProject from './pages/fundraising/DashboardFundraisingProject';
import Dev from './pages/boards/Dev';
import DashboardProjectsAll from './pages/projects/DashboardProjectsAll';
import DashboardProject from './pages/projects/DashboardProject';
import DashboardFundsMoves from './pages/funds/DashboardFundsMoves';
import AllOrganizations from './pages/admin/AllOrganizations';
import UsersAdd from './pages/admin/UsersAdd';
import OverviewDirectReports from './pages/admin/OverviewDirectReports';
import DirectReportDetails from './pages/admin/DirectReportDetails';
import OverviewTimesheet from './pages/timesheet/OverviewTimesheet';
import OverviewAbsence from './pages/timesheet/OverviewAbsence';
import OverviewPublicHoliday from './pages/timesheet/OverviewPublicHoliday';
import PreviewWorkModel from "./pages/timesheet/PreviewWorkModel";
import UsersEdit from "./pages/admin/UsersEdit";
import Rates from "./pages/Rates";
import Contact from "./pages/Contact";
import TransfersEdit from "./pages/funds/TransfersEdit";
import TransfersView from "./pages/funds/TransfersView";
import ResetPassword from "./pages/auth/ResetPassword";
import Signup from "./pages/auth/Signup";
import Organization from "./pages/Organization";
import OrganizationSettings from "./pages/OrganizationSettings";
import { I18nProvider } from './i18n/I18nContext';
import {
  trackGoogleAnalyticsEvent,
  trackGoogleAnalyticsPageView,
  updateGoogleAnalyticsUserId,
} from './analytics/googleAnalytics';
import Health from "./pages/Health";
import OverviewApprovals from "./pages/approvals/OverviewApprovals";
import { ApprovalInboxProvider } from "./pages/approvals/ApprovalInboxContext";
import ExpenseExtraction from "./pages/funds/ExpenseExtraction";

const Layout = ({ children }: { children: ReactNode }) => {
  const { user } = useAuthContext();

  return (
    <Flex minH="100vh" direction="column">
      <Header />
      <Box as="main" flex="1" display="flex" flexDirection="column" minW={0}>
        {children}
      </Box>
      <Box as="footer" hideBelow="md" mt="auto">
        <Footer loggedIn={!!user} />
      </Box>
    </Flex>
  );
}

const GoogleAnalyticsUser = () => {
  const { user } = useAuthContext();
  const userId = user?.data?.id;

  useEffect(() => {
    updateGoogleAnalyticsUserId(userId);
  }, [userId]);

  return null;
};

const GoogleAnalyticsPageViews = () => {
  const location = useLocation();

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    trackGoogleAnalyticsPageView(path);

    if (location.pathname === '/contact') {
      trackGoogleAnalyticsEvent('view_contact_page');
    } else if (location.pathname === '/signup') {
      trackGoogleAnalyticsEvent('view_signup_page');
    }
  }, [location.pathname, location.search]);

  return null;
};

const App = () => {
  return (
    <Provider defaultTheme='light'>
      <I18nProvider>
        <AuthProvider>
          <ApprovalInboxProvider>
            <Toaster />
            <GoogleAnalyticsUser />
            <BrowserRouter>
              <GoogleAnalyticsPageViews />
              <Routes>
            <Route path="/" element={<Layout><Landing /></Layout>} />
            <Route path="/login" element={<Layout><Login /></Layout>} />
            <Route path="/signup" element={<Layout><Signup /></Layout>} />
            <Route path="/logout" element={<Layout><Logout /></Layout>} />
            <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
            <Route path="/reset-password/:token?" element={<Layout><ResetPassword /></Layout>} />
            <Route path="/profile" element={<Layout><Profile /></Layout>} />
            <Route path="/partners" element={<Layout><OverviewPartners consultant={false} /></Layout>} />
            <Route path="/partners/consultants" element={<Layout><OverviewPartners consultant={true} /></Layout>} />
            <Route path="/partners/add" element={<Layout><PartnersAdd consultant={false} /></Layout>} />
            <Route path="/partners/consultants/add" element={<Layout><PartnersAdd consultant={true} /></Layout>} />
            <Route path="/partners/edit/:id" element={<Layout><PartnersEdit /></Layout>} />
            <Route path="/partners/view/:id" element={<Layout><PartnersView /></Layout>} />
            <Route path="/partners/expenses" element={<Layout><OverviewExpenses /></Layout>} />
            <Route path="/partners/expenses/add" element={<Layout><ExpensesAdd /></Layout>} />
            <Route path="/partners/expenses/edit/:id" element={<Layout><ExpensesEdit /></Layout>} />
            <Route path="/partners/expenses/view/:id" element={<Layout><ExpensesView /></Layout>} />
            <Route path="/projects" element={<Layout><OverviewProjects /></Layout>} />
            <Route path="/projects/add" element={<Layout><ProjectsAdd /></Layout>} />
            <Route path="/projects/edit/:id" element={<Layout><ProjectsEdit /></Layout>} />
            <Route path="/projects/view/:id" element={<Layout><ProjectsView /></Layout>} />
            <Route path="/projects/overview" element={<Layout><DashboardProjectsAll /></Layout>} />
            <Route path="/projects/overview/projects/:id?" element={<Layout><DashboardProject /></Layout>} />
            <Route path="/donations/codes" element={<Layout><OverviewDonationCodes /></Layout>} />
            <Route path="/transfers/moves" element={<Layout><OverviewTransfersMoves /></Layout>} />
            <Route path="/transfers/moves/add" element={<Layout><TransfersMoveAdd /></Layout>} />
            <Route path="/transfers/moves/edit/:id" element={<Layout><TransfersMoveEdit /></Layout>} />
            <Route path="/transfers/moves/view/:id" element={<Layout><TransfersMoveView /></Layout>} />
            <Route path="/transfers/add" element={<Layout><TransfersAdd /></Layout>} />
            <Route path="/transfers/edit/:id" element={<Layout><TransfersEdit /></Layout>} />
            <Route path="/transfers/view/:id" element={<Layout><TransfersView /></Layout>} />
            <Route path="/transfers" element={<Layout><OverviewTransfers /></Layout>} />
            <Route path="/funds/moves" element={<Layout><OverviewFundsMoves /></Layout>} />
            <Route path="/funds/moves/add" element={<Layout><FundMoveAdd /></Layout>} />
            <Route path="/funds/moves/edit/:id" element={<Layout><FundMoveEdit /></Layout>} />
            <Route path="/funds/moves/view/:id" element={<Layout><FundMoveView /></Layout>} />
            <Route path="/funds/provisions" element={<Layout><DashboardProvisions /></Layout>} />
            <Route path="/funds/moves/overview" element={<Layout><DashboardFundsMoves /></Layout>} />
            <Route path="/funds/add" element={<Layout><FundsAdd /></Layout>} />
            <Route path="/funds/edit/:id" element={<Layout><FundsEdit /></Layout>} />
            <Route path="/funds/view/:id" element={<Layout><FundsView /></Layout>} />
            <Route path="/funds" element={<Layout><OverviewFunds /></Layout>} />
            <Route path="/expenses" element={<Layout><ExpenseExtraction /></Layout>} />
            <Route path="/fundraising" element={<Layout><DashboardFundraisingAll /></Layout>} />
            <Route path="/fundraising/projects/:id?" element={<Layout><DashboardFundraisingProject /></Layout>} />
            <Route path="/fundraising/commitments" element={<Layout><OverviewCommitments /></Layout>} />
            <Route path="/fundraising/commitments/add" element={<Layout><CommitmentsAdd /></Layout>} />
            <Route path="/fundraising/commitments/edit/:id" element={<Layout><CommitmentsEdit /></Layout>} />
            <Route path="/fundraising/commitments/view/:id" element={<Layout><CommitmentsView /></Layout>} />
            <Route path="/fundraising/donors" element={<Layout><OverviewDonors /></Layout>} />
            <Route path="/fundraising/donors/add" element={<Layout><DonorForm /></Layout>} />
            <Route path="/fundraising/donors/edit/:id" element={<Layout><DonorForm /></Layout>} />
            <Route path="/fundraising/donors/view/:id" element={<Layout><DonorForm viewOnly /></Layout>} />
            <Route path="/timesheet/:id?" element={<Layout><OverviewTimesheet /></Layout>} />
            <Route path="/timesheet/absences" element={<Layout><OverviewAbsence /></Layout>} />
            <Route path="/holidays" element={<Layout><OverviewPublicHoliday /></Layout>} />
            <Route path="/timesheet/holidays" element={<Navigate to='/holidays' replace />} />
            <Route path="/timesheet/worktime" element={<Layout><PreviewWorkModel /></Layout>} />
            <Route path="/approvals" element={<Layout><OverviewApprovals /></Layout>} />
            <Route path="/admin/users" element={<Navigate to='/organization' replace />} />
            <Route path="/admin/organizations" element={<Layout><AllOrganizations /></Layout>} />
            <Route path="/admin/users/add" element={<Layout><RequireAdmin><UsersAdd /></RequireAdmin></Layout>} />
            <Route path="/admin/users/direct-reports" element={<Layout><OverviewDirectReports /></Layout>} />
            <Route path="/admin/users/direct-reports/:id" element={<Layout><DirectReportDetails /></Layout>} />
            <Route path="/admin/users/edit/:id" element={<Layout><RequireAdmin><UsersEdit /></RequireAdmin></Layout>} />
            <Route path="/info" element={<Layout><Info /></Layout>} />
            <Route path="/health" element={<Health />} />
            <Route path="/dev" element={<Layout><Dev /></Layout>} />
            <Route path="/rates" element={<Layout><Rates /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/organization" element={<Layout><Organization /></Layout>} />
            <Route path="/organization/settings" element={<Layout><OrganizationSettings /></Layout>} />
            <Route path="*" element={<Layout><NotFound /></Layout>} />
              </Routes>
            </BrowserRouter>
          </ApprovalInboxProvider>
        </AuthProvider>
      </I18nProvider>
    </Provider>
  );
}

export default App
